import React, {useState, useEffect, useCallback} from "react";
import {
    UploadCloud,
    Loader2,
    Check,
    X,
    Trash2,
    Plus,
    ChevronDown,
    ChevronUp,
    Clock,
    RefreshCw,
    Filter,
    AlertTriangle,
    Link as LinkIcon,
    Users,
    Globe,
    ToggleLeft,
    ToggleRight
} from "lucide-react";

const UpdateAalimPage = () => {
    // File and link management
    const [files, setFiles] = useState([]);
    const [links, setLinks] = useState([]);

    // Sect Management
    const [selectedSects, setSelectedSects] = useState([]);
    const [isAllSects, setIsAllSects] = useState(false); // NEW: State for toggle

    // Task status
    const [taskId, setTaskId] = useState(null);
    const [status, setStatus] = useState("");
    const [progress, setProgress] = useState(0);
    const [currentFile, setCurrentFile] = useState("");
    const [error, setError] = useState("");
    const [phase, setPhase] = useState(""); // NEW: Current phase (loading/embedding/distribution)
    const [detail, setDetail] = useState(""); // NEW: Granular detail (e.g., "chunks 2100/4200")

    // Task history
    const [tasks, setTasks] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [historyExpanded, setHistoryExpanded] = useState(false);
    const [statusFilter, setStatusFilter] = useState("");
    const [selectedTask, setSelectedTask] = useState(null);

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalTasks, setTotalTasks] = useState(0);
    const [pageSize] = useState(10);

    // Book search
    const [searchQuery, setSearchQuery] = useState("");
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [showSearchModal, setShowSearchModal] = useState(false);

    const apiUrl = "https://api.digitalaalim.com";
    const accessToken = localStorage.getItem("accessToken");

    /* ----------------------------------------------------------------
     * File handling
     * ---------------------------------------------------------------- */
    const handleFileSelection = (evt) => {
        const newFiles = Array.from(evt.target.files);
        setFiles((prev) => [...prev, ...newFiles]);
    };

    const removeFile = (idx) => {
        setFiles((prev) => prev.filter((_, i) => i !== idx));
    };

    /* ----------------------------------------------------------------
     * Link handling
     * ---------------------------------------------------------------- */
    const addLink = () => {
        setLinks((prev) => [...prev, ""]);
    };

    const updateLink = (index, newValue) => {
        setLinks((prev) => prev.map((l, i) => (i === index ? newValue : l)));
    };

    const removeLink = (index) => {
        setLinks((prev) => prev.filter((_, i) => i !== index));
    };

    /* ----------------------------------------------------------------
     * Upload handling (UPDATED)
     * ---------------------------------------------------------------- */
    const startUpdate = async () => {
        // Validation: Ensure files or links exist
        if (!files.length && !links.length) {
            alert("Please select at least one file or add a link.");
            return;
        }

        // Validation: If NOT "All Sects", ensure user selected at least one sect
        if (!isAllSects && selectedSects.length === 0) {
            alert("Please select at least one sect.");
            return;
        }

        try {
            setError("");
            setStatus("Uploading");
            setIsLoading(true);

            const formData = new FormData();

            // Append Files and Links
            files.forEach((f) => formData.append("files", f));
            links.forEach((lnk) => formData.append("links", lnk));

            // LOGIC UPDATE: Handle Sects
            // If "Update All Sects" is OFF, we append the specific selected sects.
            // If "Update All Sects" is ON, we do NOT append 'sects',
            // relying on backend logic: "If None, updates all sects."
            if (!isAllSects) {
                // We loop and append 'sects' for each selection to create the array
                selectedSects.forEach((s) => formData.append("sects", s));
            }

            // ALWAYS use the update_all_sects_model endpoint
            const endpoint = `${apiUrl}/update_all_sects_model/`;

            const res = await fetch(endpoint, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
                body: formData,
            });

            const data = await res.json();
            if (!res.ok) throw new Error(data.detail || "Upload failed");

            setTaskId(data.task_id);
            setStatus("Processing");
            setProgress(0);
            setCurrentFile("");
            setPhase(""); // Reset phase for new task
            setDetail(""); // Reset detail for new task

            // Refresh task list
            fetchTasks();
        } catch (err) {
            setStatus("Failed");
            setError(err.message || "Error starting upload");
        } finally {
            setIsLoading(false);
        }
    };

    /* ----------------------------------------------------------------
     * Task status polling (UPDATED to handle new backend fields)
     * ---------------------------------------------------------------- */
    const pollStatus = useCallback(async () => {
        if (!taskId) return;
        try {
            const res = await fetch(`${apiUrl}/task-status/${taskId}`, {
                headers: {Authorization: `Bearer ${accessToken}`},
            });

            const data = await res.json();
            if (!res.ok) throw new Error(data.detail || "Failed to fetch task status");

            console.log("Poll response:", data); // Debug log

            // Update all status fields from response
            const newStatus = data.status || "";
            setStatus(newStatus);
            setProgress(data.progress || 0);
            setCurrentFile(data.current_file || "");
            
            // Capture phase and detail for granular progress display
            setPhase(data.phase || "");
            setDetail(data.detail || "");

            if (newStatus === "Failed") {
                setError(data.error || "Unknown error");
            }
        } catch (err) {
            console.error("Error polling status:", err);
        }
    }, [taskId, accessToken]);

    // Helper to check if status indicates task is still running
    const isTaskRunning = (taskStatus) => {
        if (!taskStatus) return false;
        const completedStatuses = ["Completed", "Failed", "Cancelled"];
        return !completedStatuses.includes(taskStatus);
    };

    // Polling effect - runs when we have a taskId and task is still running
    useEffect(() => {
        let intervalId = null;
        
        const shouldPoll = taskId && isTaskRunning(status);
        
        console.log("Polling check:", { taskId, status, shouldPoll }); // Debug
        
        if (shouldPoll) {
            // Initial poll immediately
            pollStatus();
            
            // Then poll every 2 seconds
            intervalId = setInterval(pollStatus, 2000);
        }
        
        return () => {
            if (intervalId) {
                clearInterval(intervalId);
            }
        };
    }, [taskId, status, pollStatus]);

    // Refresh task list when task completes or fails
    useEffect(() => {
        if (status === "Completed" || status === "Failed") {
            fetchTasks();
        }
    }, [status]);

    // Auto-refresh task history while processing to show progress in table
    useEffect(() => {
        let refreshInterval = null;
        
        if (taskId && isTaskRunning(status)) {
            // Refresh task list every 5 seconds while processing
            refreshInterval = setInterval(() => {
                fetchTasks(statusFilter, currentPage);
            }, 5000);
        }
        
        return () => {
            if (refreshInterval) {
                clearInterval(refreshInterval);
            }
        };
    }, [status, taskId, statusFilter, currentPage]);

    /* ----------------------------------------------------------------
     * Task history functions
     * ---------------------------------------------------------------- */
    const fetchTasks = async (filter = statusFilter, page = currentPage) => {
        setIsLoading(true);
        try {
            let endpoint = filter
                ? `${apiUrl}/tasks/filter/?status=${filter}`
                : `${apiUrl}/tasks/`;

            const res = await fetch(endpoint, {
                headers: {Authorization: `Bearer ${accessToken}`},
            });

            if (!res.ok) {
                throw new Error("Failed to fetch tasks");
            }

            const data = await res.json();
            const allTasks = data.tasks || [];

            // Calculate pagination on client side
            const total = allTasks.length;
            const calculatedTotalPages = Math.ceil(total / pageSize);
            const startIndex = (page - 1) * pageSize;
            const endIndex = startIndex + pageSize;
            const paginatedTasks = allTasks.slice(startIndex, endIndex);

            setTasks(paginatedTasks);
            setTotalPages(calculatedTotalPages);
            setTotalTasks(total);
            setCurrentPage(page);
        } catch (err) {
            console.error("Error fetching tasks:", err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleStatusFilterChange = (status) => {
        setStatusFilter(status);
        setCurrentPage(1);
        fetchTasks(status, 1);
    };

    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
        fetchTasks(statusFilter, newPage);
    };

    /* ----------------------------------------------------------------
     * Book search functions
     * ---------------------------------------------------------------- */
    const searchBooks = async () => {
        if (!searchQuery.trim()) {
            alert("Please enter a search query");
            return;
        }

        setIsSearching(true);
        try {
            const res = await fetch(`${apiUrl}/search-books/?query=${encodeURIComponent(searchQuery)}`, {
                headers: {Authorization: `Bearer ${accessToken}`},
            });

            if (!res.ok) {
                throw new Error("Failed to search books");
            }

            const data = await res.json();
            setSearchResults(data.results || []);
            setShowSearchModal(true);
        } catch (err) {
            console.error("Error searching books:", err);
            alert("Failed to search books. Please try again.");
        } finally {
            setIsSearching(false);
        }
    };

    const closeSearchModal = () => {
        setShowSearchModal(false);
        setSearchQuery("");
        setSearchResults([]);
    };

    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        const date = new Date(dateString);
        return date.toLocaleString();
    };

    const getTaskDetails = async (taskId) => {
        try {
            const res = await fetch(`${apiUrl}/task-status/${taskId}`, {
                headers: {Authorization: `Bearer ${accessToken}`},
            });

            if (!res.ok) {
                throw new Error("Failed to fetch task details");
            }

            const data = await res.json();
            setSelectedTask(data);
        } catch (err) {
            console.error("Error fetching task details:", err);
        }
    };

    // Fetch tasks when component mounts
    useEffect(() => {
        fetchTasks(statusFilter, currentPage);
    }, []);

    return (
        <div className="space-y-8">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-gray-800">Update Digital Aalim</h1>
                    <p className="mt-2 text-gray-600">
                        Enhance the intelligence of Digital Aalim with additional information
                    </p>
                </div>

                {/* Book Search */}
                <div className="flex items-center gap-2">
                    <div className="relative">
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && searchBooks()}
                            placeholder="Search uploaded books..."
                            className="w-64 border border-gray-300 rounded-md pl-3 pr-10 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <button
                            onClick={searchBooks}
                            disabled={isSearching}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-blue-600"
                        >
                            {isSearching ? (
                                <Loader2 size={18} className="animate-spin"/>
                            ) : (
                                <LinkIcon size={18}/>
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {/* Content area */}
            <div className="grid md:grid-cols-3 gap-6">
                {/* File Upload Section */}
                <div className="md:col-span-1 bg-white rounded-xl shadow-sm p-6">
                    <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                        <UploadCloud className="mr-2 text-blue-600" size={20}/>
                        1. Upload Files
                    </h2>

                    <div
                        className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:bg-gray-50 transition-colors cursor-pointer">
                        <input
                            id="file-upload"
                            type="file"
                            multiple
                            className="hidden"
                            onChange={handleFileSelection}
                        />
                        <label htmlFor="file-upload" className="cursor-pointer">
                            <UploadCloud className="mx-auto text-gray-400" size={36}/>
                            <p className="mt-2 text-sm text-gray-600">Drag files here or click to browse</p>
                            <p className="text-xs text-gray-500 mt-1">
                                Upload documents, PDFs, or text files
                            </p>
                        </label>
                    </div>

                    {files.length > 0 && (
                        <div className="mt-4">
                            <h3 className="text-sm font-medium text-gray-700 mb-2">Selected Files ({files.length})</h3>
                            <ul className="max-h-60 overflow-y-auto space-y-2">
                                {files.map((file, idx) => (
                                    <li
                                        key={idx}
                                        className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded-md text-sm"
                                    >
                                        <div className="truncate max-w-[180px]">{file.name}</div>
                                        <button
                                            onClick={() => removeFile(idx)}
                                            className="text-gray-500 hover:text-red-500 transition-colors"
                                        >
                                            <Trash2 size={16}/>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>

                {/* Link and Sect Selection Section */}
                <div className="md:col-span-1 space-y-6">
                    {/* Links Input */}
                    <div className="bg-white rounded-xl shadow-sm p-6">
                        <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                            <LinkIcon className="mr-2 text-blue-600" size={20}/>
                            2. Add External Links
                        </h2>

                        {links.map((link, idx) => (
                            <div key={idx} className="flex items-center mb-2">
                                <input
                                    type="url"
                                    value={link}
                                    onChange={(e) => updateLink(idx, e.target.value)}
                                    placeholder="https://example.com/data.csv"
                                    className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                                <button
                                    onClick={() => removeLink(idx)}
                                    className="ml-2 text-gray-500 hover:text-red-500"
                                >
                                    <Trash2 size={16}/>
                                </button>
                            </div>
                        ))}

                        <button
                            onClick={addLink}
                            className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800 mt-2"
                        >
                            <Plus size={16} className="mr-1"/>
                            Add Link
                        </button>
                    </div>

                    {/* Sect Selection (UPDATED) */}
                    <div className="bg-white rounded-xl shadow-sm p-6 relative transition-all">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-lg font-semibold text-gray-800 flex items-center">
                                <Users className="mr-2 text-blue-600" size={20}/>
                                3. Target Audience
                            </h2>
                        </div>

                        {/* Update All Sects Switch */}
                        <div
                            className={`mb-4 p-3 rounded-lg border transition-colors cursor-pointer flex items-center justify-between ${isAllSects ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-transparent'}`}
                            onClick={() => setIsAllSects(!isAllSects)}
                        >
                            <div className="flex items-center">
                                <Globe className={`mr-2 ${isAllSects ? 'text-blue-600' : 'text-gray-400'}`} size={20}/>
                                <span
                                    className={`text-sm font-medium ${isAllSects ? 'text-blue-800' : 'text-gray-600'}`}>
                                    Update All Sects
                                </span>
                            </div>
                            {isAllSects ? (
                                <ToggleRight className="text-blue-600" size={28}/>
                            ) : (
                                <ToggleLeft className="text-gray-300" size={28}/>
                            )}
                        </div>

                        <div
                            className={`transition-opacity duration-300 ${isAllSects ? 'opacity-40 pointer-events-none' : 'opacity-100'}`}>
                            <p className="text-xs text-gray-500 mb-2">Or select specific sects (multiple selection
                                allowed):</p>
                            {/* ... inside the grid container for sects ... */}
                            <div className="grid grid-cols-2 gap-2">
                                {["Hanafi", "Jafari", "Hanbali", "Shafi", "Malaki", "Salafi"].map((sectOption) => {
                                    const isSelected = selectedSects.includes(sectOption);
                                    return (
                                        <button
                                            key={sectOption}
                                            disabled={isAllSects}
                                            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                                                isSelected
                                                    ? "bg-blue-600 text-white"
                                                    : "bg-gray-100 text-gray-800 hover:bg-gray-200"
                                            }`}
                                            onClick={() => {
                                                setSelectedSects(prev =>
                                                    prev.includes(sectOption)
                                                        ? prev.filter(s => s !== sectOption) // Unselect
                                                        : [...prev, sectOption] // Select
                                                );
                                            }}
                                        >
                                            {sectOption}
                                            {isSelected && <Check size={14} className="inline ml-2 mb-0.5"/>}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Upload Button + Progress Section */}
                <div className="md:col-span-1 space-y-6">
                    {/* Upload Button */}
                    <div className="bg-white rounded-xl shadow-sm p-6">
                        <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                            <UploadCloud className="mr-2 text-blue-600" size={20}/>
                            4. Update Model
                        </h2>

                        <button
                            onClick={startUpdate}
                            disabled={isLoading || isTaskRunning(status) || (!isAllSects && selectedSects.length === 0) || (!files.length && !links.length)}
                            className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-md font-medium transition-colors ${
                                isLoading || isTaskRunning(status) || (!isAllSects && selectedSects.length === 0) || (!files.length && !links.length)
                                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                                    : "bg-blue-600 text-white hover:bg-blue-700"
                            }`}
                        >
                            {isLoading || isTaskRunning(status) ? (
                                <>
                                    <Loader2 className="animate-spin" size={18}/>
                                    {isTaskRunning(status) ? "Processing…" : "Uploading…"}
                                </>
                            ) : (
                                <>
                                    <UploadCloud size={18}/>
                                    {isAllSects
                                        ? "Update All Sects"
                                        : selectedSects.length > 0
                                            ? `Update ${selectedSects.length} Selected Sect(s)`
                                            : "Select Sects to Update"}
                                </>
                            )}
                        </button>

                        {/* Update the warning message below the button */}
                        {(!isAllSects && selectedSects.length === 0 && (files.length > 0 || links.length > 0)) && (
                            <p className="text-amber-600 text-xs mt-2 flex items-center">
                                <AlertTriangle size={14} className="mr-1"/>
                                Please select at least one sect or enable "Update All"
                            </p>
                        )}
                    </div>

                    {/* Progress Display (UPDATED with phase and detail) */}
                    {taskId && (
                        <div className="bg-white rounded-xl shadow-sm p-6">
                            <h2 className="text-lg font-semibold text-gray-800 mb-4">Update Progress</h2>

                            <div className="space-y-4">
                                {/* Phase and Status Display */}
                                <div className="flex justify-between items-start text-sm mb-1">
                                    <div className="flex flex-col">
                                        <span className="font-medium text-gray-800">
                                            {phase === "loading" ? (
                                                "Loading Files"
                                            ) : phase === "embedding" ? (
                                                "Generating Embeddings"
                                            ) : phase === "distribution" ? (
                                                "Distributing to Sects"
                                            ) : status === "Completed" ? (
                                                "Completed"
                                            ) : status === "Failed" ? (
                                                "Failed"
                                            ) : isTaskRunning(status) ? (
                                                status || "Processing"
                                            ) : (
                                                status || "Initializing"
                                            )}
                                        </span>
                                        {/* Show granular detail (e.g., "chunks 2100/4200") */}
                                        {detail && isTaskRunning(status) && (
                                            <span className="text-xs text-gray-500 mt-0.5">
                                                {detail}
                                            </span>
                                        )}
                                        {/* Show current file if available */}
                                        {currentFile && isTaskRunning(status) && !detail && (
                                            <span className="text-xs text-gray-500 mt-0.5 truncate max-w-[200px]">
                                                {currentFile}
                                            </span>
                                        )}
                                    </div>
                                    <span className="font-semibold text-blue-600">{progress.toFixed(0)}%</span>
                                </div>

                                {/* Progress Bar */}
                                <div className="w-full h-2.5 bg-gray-200 rounded-full overflow-hidden">
                                    <div
                                        className={`h-full rounded-full transition-all duration-500 ease-out ${
                                            status === "Failed" 
                                                ? "bg-red-500" 
                                                : status === "Completed"
                                                    ? "bg-green-500"
                                                    : "bg-blue-600"
                                        }`}
                                        style={{width: `${progress}%`}}
                                    />
                                </div>

                                {/* Phase Indicator Pills */}
                                {isTaskRunning(status) && (
                                    <div className="flex items-center gap-2 mt-3">
                                        <span className={`text-xs px-2 py-1 rounded-full ${
                                            phase === "loading" 
                                                ? "bg-blue-100 text-blue-700 font-medium" 
                                                : progress >= 5 
                                                    ? "bg-green-100 text-green-700" 
                                                    : "bg-gray-100 text-gray-500"
                                        }`}>
                                            Loading
                                        </span>
                                        <span className="text-gray-300">→</span>
                                        <span className={`text-xs px-2 py-1 rounded-full ${
                                            phase === "embedding" 
                                                ? "bg-blue-100 text-blue-700 font-medium" 
                                                : progress >= 30 
                                                    ? "bg-green-100 text-green-700" 
                                                    : "bg-gray-100 text-gray-500"
                                        }`}>
                                            Embeddings
                                        </span>
                                        <span className="text-gray-300">→</span>
                                        <span className={`text-xs px-2 py-1 rounded-full ${
                                            phase === "distribution" 
                                                ? "bg-blue-100 text-blue-700 font-medium" 
                                                : progress >= 90 
                                                    ? "bg-green-100 text-green-700" 
                                                    : "bg-gray-100 text-gray-500"
                                        }`}>
                                            Distribution
                                        </span>
                                    </div>
                                )}

                                {status === "Completed" && (
                                    <div className="flex items-center text-green-600 bg-green-50 px-3 py-2 rounded-md">
                                        <Check size={16} className="mr-2"/>
                                        <span className="text-sm font-medium">Model updated successfully!</span>
                                    </div>
                                )}

                                {status === "Failed" && (
                                    <div className="flex items-center text-red-600 bg-red-50 px-3 py-2 rounded-md">
                                        <AlertTriangle size={16} className="mr-2"/>
                                        <span className="text-sm font-medium">
                                            {error || "Something went wrong."}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Task History Section */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div
                    className="flex justify-between items-center p-6 cursor-pointer border-b border-gray-100"
                    onClick={() => setHistoryExpanded(!historyExpanded)}
                >
                    <h2 className="text-lg font-semibold text-gray-800 flex items-center">
                        <Clock className="mr-2 text-blue-600" size={20}/>
                        Task History
                    </h2>
                    <button className="text-gray-500">
                        {historyExpanded ? <ChevronUp size={20}/> : <ChevronDown size={20}/>}
                    </button>
                </div>

                {historyExpanded && (
                    <div className="p-6">
                        <div className="flex flex-wrap justify-between mb-4 gap-2">
                            <div className="flex items-center gap-2">
                                <Filter size={16} className="text-gray-500"/>
                                <select
                                    value={statusFilter}
                                    onChange={(e) => handleStatusFilterChange(e.target.value)}
                                    className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="">All Tasks</option>
                                    <option value="Processing">Processing</option>
                                    <option value="Completed">Completed</option>
                                    <option value="Failed">Failed</option>
                                </select>

                                {totalTasks > 0 && (
                                    <span className="text-sm text-gray-500">
                                        ({totalTasks} total)
                                    </span>
                                )}
                            </div>

                            <button
                                onClick={() => fetchTasks(statusFilter, currentPage)}
                                className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800"
                            >
                                <RefreshCw size={14} className="mr-1"/>
                                Refresh
                            </button>
                        </div>

                        {isLoading ? (
                            <div className="flex justify-center py-12">
                                <Loader2 className="animate-spin text-blue-600" size={30}/>
                            </div>
                        ) : tasks.length === 0 ? (
                            <div className="text-center py-12 text-gray-500">
                                <p>No tasks found. Try changing the filter or refresh.</p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead>
                                    <tr>
                                        <th className="px-4 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Status
                                        </th>
                                        <th className="px-4 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Progress
                                        </th>
                                        <th className="px-4 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Current File
                                        </th>
                                        <th className="px-4 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Created
                                        </th>
                                        <th className="px-4 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Actions
                                        </th>
                                    </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                    {tasks.map((task) => (
                                        <tr key={task._id} className="hover:bg-gray-50">
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <div className="flex flex-col">
                                                    <span
                                                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                                            task.status === "Completed"
                                                                ? "bg-green-100 text-green-800"
                                                                : task.status === "Failed"
                                                                    ? "bg-red-100 text-red-800"
                                                                    : "bg-blue-100 text-blue-800"
                                                        }`}
                                                    >
                                                        {task.status}
                                                    </span>
                                                    {/* Show phase for processing tasks */}
                                                    {task.status === "Processing" && task.phase && (
                                                        <span className="text-xs text-gray-500 mt-1 capitalize">
                                                            {task.phase}
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <div className="flex flex-col">
                                                    <div className="flex items-center">
                                                        <div className="w-16 bg-gray-200 rounded-full h-1.5 mr-2">
                                                            <div
                                                                className={`h-1.5 rounded-full transition-all duration-300 ${
                                                                    task.status === "Failed" 
                                                                        ? "bg-red-500" 
                                                                        : task.status === "Completed"
                                                                            ? "bg-green-500"
                                                                            : "bg-blue-600"
                                                                }`}
                                                                style={{width: `${task.progress || 0}%`}}
                                                            />
                                                        </div>
                                                        <span className="text-xs text-gray-500">
                                                            {task.progress ? task.progress.toFixed(0) : 0}%
                                                        </span>
                                                    </div>
                                                    {/* Show detail for processing tasks (e.g., "chunks 2100/4200") */}
                                                    {task.status === "Processing" && task.detail && (
                                                        <span className="text-xs text-gray-400 mt-0.5">
                                                            {task.detail}
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 max-w-xs truncate">
                                                {task.current_file || "—"}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                                <div className="flex items-center">
                                                    <Clock size={14} className="mr-1"/>
                                                    {formatDate(task.created_at)}
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm">
                                                <button
                                                    onClick={() => getTaskDetails(task._id)}
                                                    className="text-blue-600 hover:text-blue-800 hover:underline"
                                                >
                                                    View Details
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* Pagination */}
                        {!isLoading && totalTasks > 0 && (
                            <div
                                className="flex flex-col sm:flex-row items-center justify-between mt-6 pt-4 border-t gap-3">
                                <div className="text-sm text-gray-600">
                                    Showing {tasks.length > 0 ? ((currentPage - 1) * pageSize + 1) : 0} to {Math.min(currentPage * pageSize, totalTasks)} of {totalTasks} tasks
                                </div>
                                {totalPages > 1 && (
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => handlePageChange(1)}
                                            disabled={currentPage === 1}
                                            className="px-3 py-1.5 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                                        >
                                            First
                                        </button>
                                        <button
                                            onClick={() => handlePageChange(currentPage - 1)}
                                            disabled={currentPage === 1}
                                            className="px-3 py-1.5 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                                        >
                                            Previous
                                        </button>
                                        <span
                                            className="px-3 py-1.5 text-sm border border-blue-500 bg-blue-50 text-blue-700 rounded-md font-medium">
                                            {currentPage} / {totalPages}
                                        </span>
                                        <button
                                            onClick={() => handlePageChange(currentPage + 1)}
                                            disabled={currentPage === totalPages}
                                            className="px-3 py-1.5 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                                        >
                                            Next
                                        </button>
                                        <button
                                            onClick={() => handlePageChange(totalPages)}
                                            disabled={currentPage === totalPages}
                                            className="px-3 py-1.5 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                                        >
                                            Last
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Task Details Modal */}
                        {selectedTask && (
                            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                                <div className="bg-white rounded-lg max-w-lg w-full max-h-[80vh] overflow-y-auto p-6">
                                    <div className="flex justify-between items-center mb-6">
                                        <h3 className="text-xl font-semibold text-gray-800">
                                            Task Details
                                        </h3>
                                        <button
                                            onClick={() => setSelectedTask(null)}
                                            className="text-gray-500 hover:text-gray-700"
                                        >
                                            <X size={20}/>
                                        </button>
                                    </div>

                                    <div className="space-y-6">
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <h4 className="text-sm font-medium text-gray-500">Status</h4>
                                                <p className="mt-1">
                                                    <span
                                                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                                            selectedTask.status === "Completed"
                                                                ? "bg-green-100 text-green-800"
                                                                : selectedTask.status === "Failed"
                                                                    ? "bg-red-100 text-red-800"
                                                                    : "bg-blue-100 text-blue-800"
                                                        }`}
                                                    >
                                                        {selectedTask.status}
                                                    </span>
                                                </p>
                                                {/* Show phase if available */}
                                                {selectedTask.phase && (
                                                    <p className="mt-1 text-xs text-gray-500 capitalize">
                                                        Phase: {selectedTask.phase}
                                                    </p>
                                                )}
                                            </div>

                                            <div>
                                                <h4 className="text-sm font-medium text-gray-500">Progress</h4>
                                                <div className="flex items-center mt-1">
                                                    <div className="w-full bg-gray-200 rounded-full h-1.5 mr-2">
                                                        <div
                                                            className={`h-1.5 rounded-full transition-all duration-300 ${
                                                                selectedTask.status === "Failed"
                                                                    ? "bg-red-500"
                                                                    : selectedTask.status === "Completed"
                                                                        ? "bg-green-500"
                                                                        : "bg-blue-600"
                                                            }`}
                                                            style={{width: `${selectedTask.progress || 0}%`}}
                                                        />
                                                    </div>
                                                    <span className="text-xs text-gray-500">
                                                        {selectedTask.progress ? selectedTask.progress.toFixed(0) : 0}%
                                                    </span>
                                                </div>
                                                {/* Show detail if available (e.g., "chunks 2100/4200") */}
                                                {selectedTask.detail && (
                                                    <p className="mt-1 text-xs text-gray-500">
                                                        {selectedTask.detail}
                                                    </p>
                                                )}
                                            </div>
                                        </div>

                                        <div>
                                            <h4 className="text-sm font-medium text-gray-500">Current File</h4>
                                            <p className="mt-1 text-sm text-gray-800 break-all">
                                                {selectedTask.current_file || "—"}
                                            </p>
                                        </div>

                                        <div>
                                            <h4 className="text-sm font-medium text-gray-500">Sect</h4>
                                            <p className="mt-1 text-sm text-gray-800">
                                                {selectedTask.sect || "—"}
                                            </p>
                                        </div>

                                        <div className="grid grid-cols-2 gap-4">
                                            {selectedTask.created_at && (
                                                <div>
                                                    <h4 className="text-sm font-medium text-gray-500">Created</h4>
                                                    <p className="mt-1 text-sm text-gray-800">
                                                        {formatDate(selectedTask.created_at)}
                                                    </p>
                                                </div>
                                            )}

                                            {selectedTask.updated_at && (
                                                <div>
                                                    <h4 className="text-sm font-medium text-gray-500">Last Updated</h4>
                                                    <p className="mt-1 text-sm text-gray-800">
                                                        {formatDate(selectedTask.updated_at)}
                                                    </p>
                                                </div>
                                            )}
                                        </div>

                                        {selectedTask.result && (
                                            <div>
                                                <h4 className="text-sm font-medium text-gray-500">Result</h4>
                                                <p className="mt-1 text-sm text-green-600 bg-green-50 p-2 rounded">
                                                    {selectedTask.result}
                                                </p>
                                            </div>
                                        )}

                                        {selectedTask.error && (
                                            <div>
                                                <h4 className="text-sm font-medium text-gray-500">Error</h4>
                                                <p className="mt-1 text-sm text-red-600 bg-red-50 p-2 rounded">
                                                    {selectedTask.error}
                                                </p>
                                            </div>
                                        )}
                                    </div>

                                    <div className="mt-6 flex justify-end">
                                        <button
                                            onClick={() => setSelectedTask(null)}
                                            className="px-4 py-2 bg-gray-100 text-gray-800 rounded-md hover:bg-gray-200 transition-colors"
                                        >
                                            Close
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Book Search Results Modal */}
            {showSearchModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div
                        className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[80vh] overflow-hidden flex flex-col">
                        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
                            <h2 className="text-xl font-bold text-gray-800">
                                Search Results for "{searchQuery}"
                            </h2>
                            <button
                                onClick={closeSearchModal}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <X size={24}/>
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto p-6">
                            {searchResults.length === 0 ? (
                                <div className="text-center py-12">
                                    <LinkIcon size={48} className="mx-auto text-gray-400 mb-4"/>
                                    <h3 className="text-lg font-medium text-gray-800 mb-2">
                                        No books found
                                    </h3>
                                    <p className="text-gray-600">
                                        The book "{searchQuery}" has not been uploaded yet.
                                    </p>
                                    <p className="text-sm text-green-600 mt-2">
                                        ✓ You can safely upload this book
                                    </p>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                                        <div className="flex items-start">
                                            <AlertTriangle size={20} className="text-amber-600 mr-2 mt-0.5"/>
                                            <div>
                                                <h4 className="text-sm font-medium text-amber-800">
                                                    Found {searchResults.length} matching {searchResults.length === 1 ? 'book' : 'books'}
                                                </h4>
                                                <p className="text-xs text-amber-700 mt-1">
                                                    These books may already be in the system
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    {searchResults.map((result, idx) => (
                                        <div
                                            key={idx}
                                            className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
                                        >
                                            <div className="flex items-start justify-between">
                                                <div className="flex-1">
                                                    <h3 className="font-semibold text-gray-900 mb-1">
                                                        {result.title || result.filename || "Untitled"}
                                                    </h3>

                                                    {result.author && (
                                                        <p className="text-sm text-gray-600 mb-2">
                                                            <span className="font-medium">Author:</span> {result.author}
                                                        </p>
                                                    )}

                                                    {result.sect && (
                                                        <span
                                                            className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                            {result.sect}
                                                        </span>
                                                    )}

                                                    <div className="mt-2 flex items-center gap-4 text-xs text-gray-500">
                                                        {result.uploaded_at && (
                                                            <div className="flex items-center">
                                                                <Clock size={12} className="mr-1"/>
                                                                Uploaded: {formatDate(result.uploaded_at)}
                                                            </div>
                                                        )}
                                                        {result.file_size && (
                                                            <div>
                                                                Size: {(result.file_size / 1024).toFixed(2)} KB
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                <div className="ml-4">
                                                    <span
                                                        className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                        <Check size={12} className="mr-1"/>
                                                        Uploaded
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="sticky bottom-0 bg-gray-50 px-6 py-4 border-t flex justify-end gap-3">
                            <button
                                onClick={closeSearchModal}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                            >
                                Close
                            </button>
                            {searchResults.length === 0 && (
                                <button
                                    onClick={() => {
                                        closeSearchModal();
                                        // Optionally scroll to upload section
                                        window.scrollTo({top: 0, behavior: 'smooth'});
                                    }}
                                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors"
                                >
                                    Upload This Book
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UpdateAalimPage;